import { optimizeImageForUpload } from "./imageOptimizer";
import { ADMIN_SESSION_EXPIRED_EVENT } from "auth/AdminAuthProvider";

const API_URL =
  process.env.REACT_APP_API_URL || "https://h75b24ufwb.execute-api.us-east-1.amazonaws.com";

const errorMessages = {
  admin_access_required: "لا تملك صلاحية الدخول إلى لوحة الإدارة.",
  admin_role_required: "هذه العملية متاحة لمدير النظام فقط.",
  validation_failed: "أكمل الحقول المطلوبة قبل طلب المراجعة أو النشر.",
  version_conflict: "تم تعديل السجل من مستخدم آخر. أعد تحميله ثم حاول مرة أخرى.",
  production_confirmation_required: "يلزم تأكيد النشر في بيئة الإنتاج.",
  article_must_be_published: "يجب اعتماد المقال ونشره قبل إرسال الإشعار.",
  article_notification_already_sent: "تم إرسال إشعار لهذا المقال مسبقًا.",
  notifications_not_configured: "خدمة الإشعارات غير مهيأة على الخادم.",
  onesignal_request_failed: "تعذر الاتصال بخدمة الإشعارات. حاول مرة أخرى.",
  onesignal_not_sent: "لم يقبل OneSignal طلب الإرسال. حاول مرة أخرى أو راجع إعدادات الإشعارات.",
  onesignal_no_subscribed_recipients:
    "لا يوجد حاليًا جهاز مشترك مؤهل ضمن الجمهور المستهدف. تحقق من اشتراك الجهاز ثم أعد المحاولة.",
  onesignal_no_subscribed_followers: "لا يوجد متابع مشترك لهذا الخبر حتى الآن.",
  media_storage_not_configured: "خدمة رفع الصور غير مهيأة حالياً.",
  unsupported_media_type: "ارفع صورة بصيغة JPG أو PNG أو WebP.",
  invalid_media_size: "يجب ألا يتجاوز حجم الصورة 10 ميغابايت.",
  news_automation_not_configured: "الجلب الآلي للأخبار غير مهيأ على الخادم.",
  news_sources_not_configured: "لم تتم إضافة مصادر رسمية للجلب الآلي.",
  monthly_events_automation_not_configured: "جلب أحداث Moment غير مهيأ على الخادم.",
  ticketmaster_not_configured: "لم تتم إضافة مفتاح Ticketmaster بعد.",
  ticketmaster_secret_unavailable: "تعذر قراءة مفتاح Ticketmaster الآمن.",
  ticketmaster_auth_failed: "رفض Ticketmaster مفتاح API. راجع الإعدادات.",
  ticketmaster_request_failed: "تعذر جلب الأحداث من Ticketmaster. حاول لاحقاً.",
  monthly_events_translation_failed: "تعذرت ترجمة الأحداث إلى العربية. حاول لاحقاً.",
  bedrock_mantle_request_failed: "تعذر إنشاء المسودات عبر Bedrock Mantle.",
  article_must_be_approved: "اعتمد المقال أولاً قبل توليد الصورة.",
  article_image_required_before_publish: "يجب اعتماد المسودة الآلية وتوليد صورتها قبل النشر.",
  openai_api_request_failed: "تعذر توليد الصورة عبر OpenAI. حاول مرة أخرى.",
  news_image_generation_failed: "تعذر توليد صورة الخبر. حاول مرة أخرى.",
  invalid_home_services_layout: "اختر أحد تصاميم خدمات الصفحة الرئيسية المتاحة.",
  invalid_trip_itinerary_enabled: "حدد ما إذا كان برنامج الرحلة ظاهراً أو مخفياً.",
  invalid_explore_restaurants_enabled: "حدد ما إذا كان قسم المطاعم ظاهراً أو مخفياً.",
  invalid_translation_enabled: "حدد ما إذا كانت خدمة الترجمة مفعّلة أو متوقفة.",
  invalid_translation_daily_limit: "حد المستخدم اليومي يجب أن يكون بين 1 و100.",
  invalid_translation_monthly_limit: "حد التطبيق الشهري يجب أن يكون بين 1 و1,000,000.",
  invalid_translation_cache_ttl: "مدة التخزين المؤقت يجب أن تكون بين يوم و365 يومًا.",
  translation_settings_unavailable: "تعذر حفظ إعدادات المترجم حاليًا.",
  translation_usage_unavailable: "تعذر قراءة استهلاك المترجم حاليًا.",
  invalid_analytics_period: "اختر فترة تحليل مدعومة: 7 أو 30 أو 90 يوماً.",
  invalid_analytics_platform: "منصة التحليل غير مدعومة.",
  social_platform_required: "اختر منصة واحدة على الأقل للنشر.",
  social_caption_required: "أدخل نص المنشور.",
  instagram_media_url_required: "Instagram يحتاج رابط وسائط HTTPS.",
  social_post_type_requires_instagram: "الستوري والريل متاحان لـ Instagram فقط حالياً.",
  invalid_social_post_type: "اختر نوع محتوى مدعوم.",
  invalid_social_schedule: "اختر وقتًا مستقبليًا للجدولة.",
  meta_not_connected: "لم يكتمل ربط Facebook بعد.",
  instagram_not_connected: "لم يكتمل ربط حساب Instagram الاحترافي بعد.",
  meta_publish_failed: "رفضت Meta طلب النشر. راجع ربط الحساب وصلاحيات التطبيق.",
  content_not_found: "لم يعد هذا المحتوى موجوداً.",
};

function readableError(payload, fallback) {
  const code = payload?.error;
  return errorMessages[code] || code || fallback;
}

export class AdminApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

function redirectToLogin() {
  localStorage.removeItem("msafer_admin_access_token");
  localStorage.removeItem("msafer_admin_id_token");
  window.dispatchEvent(new Event(ADMIN_SESSION_EXPIRED_EVENT));
}

export async function adminRequest(path, options = {}) {
  const token = localStorage.getItem("msafer_admin_access_token");
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) {
      redirectToLogin();
    }
    throw new AdminApiError(readableError(payload, "تعذر إكمال الطلب"), response.status, payload);
  }
  return payload;
}

export async function publicRequest(path) {
  const response = await fetch(`${API_URL}${path}`, {
    headers: { Accept: "application/json" },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new AdminApiError(
      readableError(payload, "تعذر تحميل البيانات"),
      response.status,
      payload
    );
  }
  return payload;
}

export function listResource(resource, query = {}) {
  const search = new URLSearchParams(
    Object.entries(query).filter(([, value]) => value !== "" && value != null)
  );
  return adminRequest(`/admin/${resource}${search.size ? `?${search}` : ""}`);
}

export async function listAllResource(resource, query = {}) {
  const items = [];
  let cursor = null;

  do {
    const payload = await listResource(resource, {
      ...query,
      limit: 100,
      ...(cursor ? { cursor } : {}),
    });
    if (Array.isArray(payload)) return payload;
    items.push(...unwrapItems(payload));
    cursor = payload?.nextCursor || null;
  } while (cursor);

  return { items, total: items.length, nextCursor: null };
}

export function createResource(resource, values) {
  return adminRequest(`/admin/${resource}`, {
    method: "POST",
    body: JSON.stringify(values),
  });
}

export function updateResource(resource, id, values) {
  return adminRequest(`/admin/${resource}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(values),
  });
}

export function deleteResource(resource, id) {
  return adminRequest(`/admin/${resource}/${id}`, {
    method: "DELETE",
  });
}

export function resourceAction(resource, id, action, values = {}) {
  return adminRequest(`/admin/${resource}/${id}/${action}`, {
    method: "POST",
    body: JSON.stringify(values),
  });
}

export function getNewsAutomationStatus() {
  return adminRequest("/admin/articles/automation/status");
}

export function getMonthlyEventsAutomationStatus() {
  return adminRequest("/admin/monthly-events/automation/status");
}

export async function uploadMedia({ file, entityType, entityId }) {
  const prepared = await optimizeImageForUpload(file);
  const uploadFile = prepared.file;
  const signed = await adminRequest("/admin/media/uploads", {
    method: "POST",
    body: JSON.stringify({
      entityType,
      entityId,
      contentType: uploadFile.type,
      size: uploadFile.size,
    }),
  });
  const upload = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": uploadFile.type },
    body: uploadFile,
  });
  if (!upload.ok) throw new AdminApiError("فشل رفع الصورة", upload.status);
  return { ...signed, optimization: prepared.optimization };
}

export function unwrapItems(payload) {
  return Array.isArray(payload) ? payload : payload?.items || [];
}
